import { useEffect, useState } from "react";
import type { CSSProperties, FC, PointerEvent, ReactNode } from "react";
import { Helmet } from "react-helmet-async";
import { EFFECTS } from "@/components/plumb/effects";
import { Reading } from "@/components/plumb/Reading";
import { Footer } from "./Body";
import { HERO, NAME } from "./content";
import { Crosshair } from "./Crosshair";
import { reducedMotion, useTheme } from "./hooks";
import { GRADES, PixelType } from "./PixelType";
import { Nav } from "./Top";

/** What the browser tab and a shared link say on every studio page. */
export const Head: FC = () => (
  <Helmet>
    <title>{NAME}</title>
    <meta name="description" content={HERO.first} />
    <meta property="og:title" content={NAME} />
    <meta property="og:description" content={HERO.first} />
    <meta name="twitter:title" content={NAME} />
    <meta name="twitter:description" content={HERO.first} />
  </Helmet>
);

/** The frame every page off the studio shares: the bar, the page, the footer, the pointer. */
export const Page: FC<{ children: ReactNode }> = ({ children }) => {
  const [theme] = useTheme();

  return (
    <div className="sd sd-page" data-theme={theme}>
      <Head />
      <Nav />
      <main>{children}</main>
      <Footer />
      <Crosshair />
    </div>
  );
};

/** Steps the title from its coarsest pixel grade to the plain face once, when the page opens. */
function useSharpen(ms = 150): number {
  const [grade, setGrade] = useState(() => (reducedMotion() ? GRADES.length : 0));
  useEffect(() => {
    if (grade >= GRADES.length) return;
    const id = window.setTimeout(() => setGrade(n => n + 1), ms);
    return () => window.clearTimeout(id);
  }, [grade, ms]);
  return grade;
}

interface HeadProps {
  label: string;
  lines: string[];
  children?: ReactNode;
}

/** A page's opening: a small label, the title sharpening into place, and a short text. */
export const PageHead: FC<HeadProps> = ({ label, lines, children }) => {
  const grade = useSharpen();
  return (
    <header className="sd-page-head">
      <p className="sd-label">{label}</p>
      <PixelType lines={lines} grade={grade} />
      {children}
    </header>
  );
};

interface LoupeProps {
  src: string;
  depth: string;
  alt: string;
  hint: string;
}

/** Side of the window under the pointer, in pixels. */
const SIDE = 230;

/**
 * A picture shown clean. Under the pointer a window opens onto the same
 * picture as the machine reads it; pressing switches to the next reading.
 */
export const Loupe: FC<LoupeProps> = ({ src, depth, alt, hint }) => {
  const [at, setAt] = useState({ x: 0, y: 0, w: 0.2, h: 0.2 });
  const [near, setNear] = useState(false);
  const [reading, setReading] = useState(3);
  const effect = EFFECTS[reading % EFFECTS.length];

  const track = (e: PointerEvent<HTMLDivElement>) => {
    const box = e.currentTarget.getBoundingClientRect();
    const w = SIDE / box.width;
    const h = SIDE / box.height;
    setNear(true);
    setAt({ x: (e.clientX - box.left) / box.width - w / 2, y: (e.clientY - box.top) / box.height - h / 2, w, h });
  };

  return (
    <div
      className="sd-band sd-loupe"
      onPointerMove={track}
      onPointerLeave={() => setNear(false)}
      onPointerDown={() => setReading(n => n + 1)}
    >
      <img className="sd-cover" src={src} alt={alt} />
      <div
        className="sd-lens sd-lens-loupe"
        hidden={!near}
        style={{ "--fx": at.x, "--fy": at.y, "--fw": at.w, "--fh": at.h } as CSSProperties}
      >
        <div className="sd-lens-in">
          <Reading className="sd-cover" src={src} depth={depth} effect={effect} />
        </div>
        <span className="sd-lens-tag">{effect}</span>
      </div>
      <p className="sd-bench-tag">{hint}</p>
    </div>
  );
};

/** Two lines at most, split near the middle, so a long title still fits a phone. */
export const titleLines = (title: string): string[] => {
  const words = title.split(" ");
  if (words.length < 2) return words;
  const cut = Math.ceil(words.length / 2);
  return [words.slice(0, cut).join(" "), words.slice(cut).join(" ")];
};
